import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../../../hooks/useAuth';
import api from '../../../utils/api';

const CompanySettingsPage = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    phone: '',
    website: '',
    logo: null
  });
  
  // Fetch company data
  const { data: company, isLoading } = useQuery('company', async () => {
    const response = await api.get(`/api/companies/${user.companyId}`);
    return response.data;
  });
  
  // Update form data when company data is loaded
  useEffect(() => {
    if (company) {
      setFormData({
        name: company.name || '',
        address: company.address || '',
        city: company.city || '',
        state: company.state || '',
        zipCode: company.zipCode || '',
        country: company.country || 'USA',
        phone: company.phone || '',
        website: company.website || '',
        logo: null
      });
    }
  }, [company]);
  
  // Update company mutation
  const updateCompanyMutation = useMutation(
    (data) => api.put(`/api/companies/${user.companyId}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('company');
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to update company settings');
      }
    }
  );
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    
    const data = new FormData();
    for (const [key, value] of Object.entries(formData)) {
      if (value !== null) {
        data.append(key, value);
      }
    }
    
    updateCompanyMutation.mutate(data);
  };
  
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto py-6 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Company Settings
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Manage your company information and appearance settings.
          </p>
        </div>
      </div>

      <div className="mt-8 bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">Company Information</h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            These details will be used throughout the system and on exports.
          </p>
        </div>
        
        {error && (
          <div className="mx-6 my-4 rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}
        
        {success && (
          <div className="mx-6 my-4 rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Company settings updated successfully!</h3>
              </div>
            </div>
          </div>
        )}
        
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-6 gap-6">
              <div className="col-span-6 sm:col-span-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Company Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="form-input mt-1"
                />
              </div>

              <div className="col-span-6">
                <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                  Street Address
                </label>
                <input
                  type="text"
                  name="address"
                  id="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="form-input mt-1"
                />
              </div>

              <div className="col-span-6 sm:col-span-6 lg:col-span-2">
                <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                  City
                </label>
                <input
                  type="text"
                  name="city"
                  id="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="form-input mt-1"
                />
              </div>

              <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                  State / Province
                </label>
                <input
                  type="text"
                  name="state"
                  id="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="form-input mt-1"
                />
              </div>

              <div className="col-span-6 sm:col-span-3 lg:col-span-2">
                <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700">
                  ZIP / Postal Code
                </label>
                <input
                  type="text"
                  name="zipCode"
                  id="zipCode"
                  value={formData.zipCode}
                  onChange={handleInputChange}
                  className="form-input mt-1"
                />
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                  Country
                </label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="form-input mt-1"
                >
                  <option value="USA">United States</option>
                  <option value="CAN">Canada</option>
                  <option value="MEX">Mexico</option>
                  <option value="GBR">United Kingdom</option>
                  <option value="AUS">Australia</option>
                </select>
              </div>

              <div className="col-span-6 sm:col-span-3">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="text"
                  name="phone"
                  id="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="form-input mt-1"
                />
              </div>

              <div className="col-span-6 sm:col-span-4">
                <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                  Website
                </label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-50 px-3 text-gray-500 sm:text-sm">
                    https://
                  </span>
                  <input
                    type="text"
                    name="website"
                    id="website"
                    value={formData.website?.replace(/^https?:\/\//, '')}
                    onChange={handleInputChange}
                    className="block w-full min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 px-3 py-2 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    placeholder="www.example.com"
                  />
                </div>
              </div>

              <div className="col-span-6">
                <label className="block text-sm font-medium text-gray-700">Company Logo</label>
                <div className="mt-1 flex items-center">
                  {company?.logo ? (
                    <div className="mr-4">
                      <img
                        src={company.logo}
                        alt="Company logo"
                        className="h-12 w-auto"
                      />
                    </div>
                  ) : (
                    <span className="h-12 w-12 rounded-full overflow-hidden bg-gray-100 mr-4">
                      <svg className="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </span>
                  )}
                  <div>
                    <label
                      htmlFor="logo"
                      className="cursor-pointer ml-5 bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Change
                    </label>
                    <input
                      id="logo"
                      name="logo"
                      type="file"
                      accept="image/*"
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                className="btn-primary"
                disabled={updateCompanyMutation.isLoading}
              >
                {updateCompanyMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Subscription section */}
      <div className="mt-8 bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">Subscription</h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Your current subscription plan and billing information.
          </p>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <div className="flex-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                {company?.subscription || 'FREE'} Plan
              </h3>
              <div className="mt-1 text-sm text-gray-500">
                {company?.subscriptionEndDate ? (
                  <p>Your subscription renews on {new Date(company.subscriptionEndDate).toLocaleDateString()}.</p>
                ) : (
                  <p>You are currently on the free plan.</p>
                )}
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn-primary"
                onClick={() => window.location.href = '/pricing'}
              >
                Upgrade Plan
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettingsPage;